import {DispatchRequestEntity, integrationStatus, toIntegrationStatus} from "@ots-frontend/ots-data";
import {GridColumnWithContent} from "../DataGrid/useDataGrid";
import {UseEntityDataGridProps} from "../EntityDataGrid/useEntityDataGrid";
import {useTranslation} from "react-i18next";
import {useMemo} from "react";
import {GridCellKind} from "@glideapps/glide-data-grid";
import {getFormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {ImageTextCell} from "../DataGrid/cells/ImageTextCell";

export type UseDispatchRequestDataGridColsProps = Pick<UseEntityDataGridProps<DispatchRequestEntity>, 'colWidths'>

export type UseDispatchRequestDataGridResult = {
  colsMap: GridColumnWithContent<DispatchRequestEntity>[]
}

export function useDispatchRequestDataGridCols(props: UseDispatchRequestDataGridColsProps): UseDispatchRequestDataGridResult {
  const {colWidths} = props;

  const {t} = useTranslation(["common"])

  const colsMap: GridColumnWithContent<DispatchRequestEntity>[] = useMemo(() => {
    let index = 0
    return [
      {
        title: "ID",
        width: colWidths?.at(index++) ?? 100,
        hasMenu: true,
        getContent: (d) => ({
          kind: GridCellKind.Number,
          data: d?.resourceId,
          allowOverlay: false,
          displayData: d?.resourceId?.toString() ?? ""
        })
      },
      {
        title: "Scheduled",
        width: colWidths?.at(index++) ?? 100,
        hasMenu: true,
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.scheduledDttm ? getFormattedDateTime(d.scheduledDttm) : "",
          allowOverlay: false,
          displayData: d?.scheduledDttm ? getFormattedDateTime(d.scheduledDttm) : ""
        })
      },
      {
        title: "Operator",
        width: colWidths?.at(index++) ?? 100,
        hasMenu: true,
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.operatorName ?? "",
          allowOverlay: false,
          displayData: d?.operatorName ?? ""
        })
      },
      {
        title: "Pickup Site",
        width: colWidths?.at(index++) ?? 100,
        hasMenu: true,
        group: "Pickup",
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.pickupSiteName ?? "",
          allowOverlay: false,
          displayData: d?.pickupSiteName ?? ""
        })
      },
      {
        title: "Pickup Container",
        width: colWidths?.at(index++) ?? 100,
        hasMenu: true,
        group: "Pickup",
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.pickupContainerName ?? "",
          allowOverlay: false,
          displayData: d?.pickupContainerName ?? ""
        })
      },
      {
        title: "Delivery Site",
        width: colWidths?.at(index++) ?? 100,
        hasMenu: true,
        group: "Delivery",
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.deliverySiteName ?? "",
          allowOverlay: false,
          displayData: d?.deliverySiteName ?? ""
        })
      },
      {
        title: "Delivery Container",
        width: colWidths?.at(index++) ?? 100,
        hasMenu: true,
        group: "Delivery",
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.deliveryContainerName ?? "",
          allowOverlay: false,
          displayData: d?.deliveryContainerName ?? ""
        })
      },
      {
        title: "Qty",
        width: colWidths?.at(index++) ?? 100,
        hasMenu: true,
        group: "Load",
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.quantityDisplay ?? "",
          allowOverlay: false,
          displayData: d?.quantityDisplay ?? ""
        })
      },
      {
        title: "Service Type",
        width: colWidths?.at(index++) ?? 100,
        hasMenu: true,
        group: "Load",
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.serviceTypeName ?? "",
          allowOverlay: false,
          displayData: d?.serviceTypeName ?? ""
        })
      },
      {
        title: "Freight Type",
        width: colWidths?.at(index++) ?? 100,
        hasMenu: true,
        group: "Load",
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.freightTypeName ?? "",
          allowOverlay: false,
          displayData: d?.freightTypeName ?? ""
        })
      },
      {
        title: "Status",
        width: colWidths?.at(index++) ?? 100,
        hasMenu: true,
        getContent: (d) => ({
          kind: GridCellKind.Text,
          data: d?.status.code ?? "",
          allowOverlay: false,
          displayData: d?.status ? t(d.status.displayValue.id, d.status.displayValue.defaultMessage) : ""
        })
      },
      {
        title: "External ID",
        width: colWidths?.at(index++) ?? 100,
        hasMenu: true,
        getContent: (d) => {

          let image: string | undefined
          if (d.objectIntegrationStatus) {
            let success: boolean | undefined = undefined
            Object.values(d.objectIntegrationStatus.statusMap).forEach(s => {
              success = true
              if (toIntegrationStatus(s.status) === integrationStatus.failure) {
                success = false
                return
              }
            })

            if (typeof success !== "undefined") {
              if (success) {
                image = import.meta.env.BASE_URL + "satellite.svg"
              } else {
                image = import.meta.env.BASE_URL + "alert-triangle.svg"
              }
            }
          }

          const text = (d?.externalId && d?.externalSystem ? (`${d.externalId} (${d.externalSystem})`) : d?.externalId) ?? ""

          return {
            kind: GridCellKind.Custom,
            allowOverlay: false,
            copyData: text,
            data: {
              kind: "image-text-cell",
              name: text,
              image: image,
            },
          } as ImageTextCell
        }
      }
    ]
  }, [colWidths, t])

  return {
    colsMap: colsMap
  }
}
